import { CalendarToday } from '@mui/icons-material';
import { alpha, TextField, useTheme } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import React from 'react';
import 'dayjs/locale/de';

interface FuturisticDatePickerProps {
    value?: Date;
    onChange: (date: Date | undefined) => void;
    label?: string;
    placeholder?: string;
    size?: 'small' | 'medium';
    fullWidth?: boolean;
    disabled?: boolean;
}

export default function FuturisticDatePicker({
    value,
    onChange,
    label,
    placeholder = 'Datum auswählen',
    size = 'small',
    fullWidth = true,
    disabled = false,
}: FuturisticDatePickerProps) {
    const theme = useTheme();

    const handleChange = (newValue: any) => {
        onChange(newValue ? newValue.toDate() : undefined);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
            <DatePicker
                value={value ? dayjs(value) : null}
                onChange={handleChange}
                disabled={disabled}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                    openPickerIcon: CalendarToday,
                    textField: TextField,
                }}
                slotProps={{
                    textField: {
                        size,
                        fullWidth,
                        placeholder,
                        label,
                        InputLabelProps: { shrink: true },
                        sx: {
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 33,
                                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '& fieldset': {
                                    borderColor: alpha(theme.palette.primary.main, 0.3),
                                    borderWidth: 1.5,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                },
                                '&:hover': {
                                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                                    '& fieldset': {
                                        borderColor: theme.palette.primary.main,
                                        borderWidth: 2,
                                    },
                                },
                                '&.Mui-focused': {
                                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.25)}`,
                                    '& fieldset': {
                                        borderColor: theme.palette.primary.main,
                                        borderWidth: 2,
                                        boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
                                    },
                                },
                                '&.Mui-disabled': {
                                    background: alpha(theme.palette.action.disabled, 0.05),
                                    '& fieldset': {
                                        borderColor: alpha(theme.palette.action.disabled, 0.2),
                                    },
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: theme.palette.text.secondary,
                                fontWeight: 500,
                                '&.Mui-focused': {
                                    color: theme.palette.primary.main,
                                    fontWeight: 600,
                                },
                            },
                            '& .MuiInputBase-input': {
                                color: theme.palette.text.primary,
                                fontWeight: 500,
                                '&::placeholder': {
                                    color: alpha(theme.palette.text.secondary, 0.7),
                                    opacity: 1,
                                },
                            },
                            '& .MuiSvgIcon-root': {
                                color: theme.palette.primary.main,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    color: theme.palette.primary.dark,
                                    transform: 'scale(1.1)',
                                },
                            },
                        },
                    },
                    popper: {
                        sx: {
                            '& .MuiPaper-root': {
                                borderRadius: 3,
                                background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                                boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                backdropFilter: 'blur(10px)',
                                overflow: 'hidden',
                            },
                            '& .MuiDateCalendar-root': {
                                background: 'transparent',
                            },
                            '& .MuiPickersCalendarHeader-root': {
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                color: theme.palette.primary.contrastText,
                                margin: 0,
                                padding: '16px',
                                '& .MuiPickersArrowSwitcher-root': {
                                    '& .MuiIconButton-root': {
                                        color: theme.palette.primary.contrastText,
                                        '&:hover': {
                                            backgroundColor: alpha(theme.palette.primary.contrastText, 0.1),
                                        },
                                    },
                                },
                                '& .MuiPickersCalendarHeader-label': {
                                    color: theme.palette.primary.contrastText,
                                    fontWeight: 600,
                                    fontSize: '1.1rem',
                                },
                            },
                            '& .MuiDayCalendar-weekDayLabel': {
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                                fontSize: '0.875rem',
                            },
                            '& .MuiPickersDay-root': {
                                borderRadius: 2,
                                fontWeight: 500,
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    transform: 'scale(1.05)',
                                },
                                '&.Mui-selected': {
                                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                    color: theme.palette.primary.contrastText,
                                    fontWeight: 600,
                                    '&:hover': {
                                        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                                    },
                                },
                                '&.MuiPickersDay-today': {
                                    border: `2px solid ${theme.palette.primary.main}`,
                                    fontWeight: 600,
                                },
                            },
                        },
                    },
                }}
            />
        </LocalizationProvider>
    );
}
